import { useEffect, useState, useMemo } from 'react';
import { sendScreenEvent, sendCustomEvent } from '@useblu/blu-lytics';

import { useNavigate, useParams } from 'react-router-dom';

import { getPaymentLinkById } from '@/services/api';

import { IUseSuccessReturn, IStatus } from './types';
import { ILinkProps } from '@/pages/Checkout/types/paymentLinkContextTypes';

const useSuccess = (): IUseSuccessReturn => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState<boolean>(true);
  const [getLinkData, setGetLinkData] = useState<ILinkProps | null>(null);
  const [hasCompleted, setHasCompleted] = useState<boolean>(false);

  const handleOpenLink = () => navigate(`/${id}`);

  const handleGetLinkData = async () => {
    await getPaymentLinkById(id).then(response => {
      setLoading(false);
      const data = response.data.data;
      const paymentStatus = data.last_payment_link_intent?.payment_status;
      setGetLinkData(data);

      if (['confirmed'].includes(paymentStatus)) {
        setHasCompleted(true);
      }
    });
  };

  const handleGetInstallmentValue = () => {
    const amount = getLinkData?.amount;
    const installmentValue =
      getLinkData?.last_payment_link_intent?.payment_link_transaction
        ?.installments;

    if (!amount || !installmentValue) return 0;

    return parseFloat(amount) / installmentValue;
  };

  const linkUrl = `/${id}`;

  const content: IStatus = useMemo(() => {
    const paymentStatus = getLinkData?.last_payment_link_intent?.payment_status;

    if (paymentStatus === 'confirmed') return 'confirmed';
    if (paymentStatus === 'failed') return 'failed';

    return 'processing';
  }, [getLinkData]);

  useEffect(() => {
    handleGetLinkData();
  }, []);

  useEffect(() => {
    let interval: any;

    if (!hasCompleted) {
      interval = setInterval(() => {
        handleGetLinkData();
      }, 5000);
    }

    if (hasCompleted) {
      clearInterval(interval);

      sendCustomEvent('link_blu_checkout_success', {
        payment_type: 'credit',
        link_id: id,
        installments:
          getLinkData?.last_payment_link_intent?.payment_link_transaction
            ?.installments,
        client_uuid: getLinkData?.client_uuid,
        amount: getLinkData?.amount,
        authentication_type:
          getLinkData?.last_payment_link_intent?.checkout_type,
      });
    }

    return () => clearInterval(interval);
  }, [hasCompleted]);

  const logEvent = () => {
    if (localStorage._bl_init === 'init') {
      sendScreenEvent('link_blu_checkout_success_view');
      return;
    }

    setTimeout(() => {
      logEvent();
    }, 1000);
  };

  useEffect(() => {
    logEvent();
  }, []);

  return {
    loading,
    content,
    getLinkData,
    linkUrl,
    handleOpenLink,
    handleGetInstallmentValue,
  };
};

export default useSuccess;
